import React, { useEffect, useState } from 'react'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { withTranslation } from 'react-i18next'
import moment from 'moment'

import { fetchDeviceHistory as actionFDH } from '../../../actions/device/history'
import { storeHistoryNote as actionSDHN } from '../../../actions/device/history/notes'
import { Arrow, Box, Centered, Colored, Column, Headings, Input, List, Loader, Padding, Button } from '../../../components'
import SingleNote from '../../../components/Notes/SingleNote'
import { colors } from '../../../constants'
import { getDeviceHistory, isHistoryWorking } from '../../../reducers/device/history'

function DeviceHistory(props) {
    const { device, fetchHistory, history, isWorking, storeHistoryNote, t } = props
    const [selectedEntryIndex, setSelectedEntryIndex] = useState(null)
    const [historyNotes, setHistoryNotes] = useState({})

    useEffect(() => {
        fetchHistory(device)
    }, [device, fetchHistory])

    useEffect(() => {
        const newHistoryNotes = history.reduce((out, { id }) => ({ ...out, [id]: '' }), {})

        setHistoryNotes(newHistoryNotes)
    }, [history])

    const selectedEntry = typeof selectedEntryIndex === 'number' && history[selectedEntryIndex]
    const { device_id, difference = [], id: selectedHistoryId, notes = [] } = selectedEntry || {}
    const currentNote = historyNotes[selectedHistoryId]

    const onChangeNote = ({ target: { value } }) => setHistoryNotes({ ...historyNotes, [selectedHistoryId]: value })

    const onSubmitNote = () => {
        storeHistoryNote({ note: currentNote }, device_id, selectedHistoryId)
    }

    const renderHistoryRow = (id) => {
        const item = history.find(({ id: historyId }) => historyId === id) || {}
        const { last_editor, created_at, difference: itemDifferences = {} } = item || {}

        return (
            <Padding style={selectedHistoryId === id ? { backgroundColor: colors.bluelightest } : {}}>
                <Padding bottom size="sm">
                    {t('device-history-num-changes', {
                        count: Object.keys(itemDifferences).length,
                    })}
                </Padding>
                {t('device-history-num-changes-author', {
                    author: last_editor,
                    time: moment(created_at).fromNow(),
                })}
            </Padding>
        )
    }

    return (
        <Box height="600px" width="1000px">
            {isWorking && (
                <Centered horizontal>
                    <Loader />
                </Centered>
            )}
            {!isWorking && (
                <>
                    <Column width="30%">
                        <List
                            buildKey={(index) => history[index].id}
                            data={history}
                            display="title"
                            itemRenderer={renderHistoryRow}
                            onClickRow={(index) => setSelectedEntryIndex(index)}
                        />
                    </Column>
                    <Column width="70%" noOverflow>
                        <Padding right noOverflow height="300px">
                            {!selectedEntry && <Padding left>{t('device-history-empty')}</Padding>}
                            {selectedEntry &&
                                Object.keys(difference).map((key) => (
                                    <Padding bottom left key={key}>
                                        <Column width="30%">{t(`device.${key}`)}</Column>
                                        <Column width="32%">
                                            <Colored color="red">{difference[key].old}</Colored>
                                        </Column>
                                        <Column width="6%">
                                            <Centered vertical horizontal>
                                                <Arrow direction="right" colored />
                                            </Centered>
                                        </Column>
                                        <Column width="32%">
                                            <Colored color="blue">{difference[key].new}</Colored>
                                        </Column>
                                    </Padding>
                                ))}
                        </Padding>
                        {selectedEntry && (
                            <Padding top left bottom height="300px">
                                <Padding bottom height="50px">
                                    <Headings.H2>{t('device-history-notes')}</Headings.H2>
                                </Padding>
                                <Padding bottom height="100px" noOverflow>
                                    {notes.map(({ author, created_at, id, note, sticky }) => (
                                        <SingleNote
                                            key={id}
                                            noChildren
                                            note={{
                                                id,
                                                note,
                                                user_name: author,
                                                sticky,
                                                created_at,
                                                children: [],
                                            }}
                                        />
                                    ))}
                                </Padding>
                                <Padding top height="150px">
                                    <Input size="none" type="textarea" onChange={onChangeNote} value={currentNote} />
                                    <Button onClick={onSubmitNote}>{t('save')}</Button>
                                </Padding>
                            </Padding>
                        )}
                    </Column>
                </>
            )}
        </Box>
    )
}

DeviceHistory.propTypes = {
    device: PropTypes.string.isRequired,
    fetchHistory: PropTypes.func.isRequired,
    history: PropTypes.arrayOf(
        PropTypes.shape({
            created_at: PropTypes.number,
            difference: PropTypes.shape({}),
            id: PropTypes.number,
            last_editor: PropTypes.string,
            notes: PropTypes.arrayOf(PropTypes.shape({})),
        })
    ).isRequired,
    isWorking: PropTypes.bool.isRequired,
    storeHistoryNote: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
}

DeviceHistory.defaultProps = {
    //
}

function mapStateToProps(state) {
    return {
        history: getDeviceHistory(state),
        isWorking: isHistoryWorking(state),
    }
}

function mapDispatchToProps(dispatch) {
    return bindActionCreators(
        {
            fetchHistory: actionFDH,
            storeHistoryNote: actionSDHN,
        },
        dispatch
    )
}

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(DeviceHistory))
